import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Play, 
  Pause, 
  RotateCcw, 
  ZoomIn, 
  ZoomOut, 
  Download,
  Smartphone
} from 'lucide-react';

const Demo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentView, setCurrentView] = useState('vertical');

  const stats = [
    { icon: <Download className="w-5 h-5" />, value: "1K+", label: "Downloads" },
    { icon: <Smartphone className="w-5 h-5" />, value: "Android 6.0+", label: "Compatibility" }
  ];

  return (
    <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-secondary-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            See It in Action
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Experience the modern PDF viewer interface with interactive controls and smooth animations.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Demo Interface */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-4">
                                 <div className="flex items-center justify-between">
                   <div className="flex items-center space-x-3">
                     <img src={process.env.PUBLIC_URL + '/favicon.png'} alt="PDF Viewer" className="w-6 h-6 rounded" />
                     <span className="text-white font-semibold">PDF Viewer</span>
                   </div>
                  <div className="flex items-center space-x-2">
                    <button className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <ZoomOut className="w-4 h-4 text-white" />
                    </button>
                    <button className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <ZoomIn className="w-4 h-4 text-white" />
                    </button>
                    <button className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                      <RotateCcw className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content Area */}
              <div className="p-6">
                <div className="bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-xl p-8 mb-4">
                  <div className="space-y-4">
                    <div className="h-3 bg-secondary-300 rounded w-full"></div>
                    <div className="h-3 bg-secondary-300 rounded w-3/4"></div>
                    <div className="h-3 bg-secondary-300 rounded w-1/2"></div>
                    <div className="h-3 bg-secondary-300 rounded w-5/6"></div>
                    <div className="h-3 bg-secondary-300 rounded w-2/3"></div>
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center justify-center space-x-4 mb-4">
                  <button
                    onClick={() => setCurrentView('vertical')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      currentView === 'vertical' 
                        ? 'bg-primary-600 text-white' 
                        : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                    }`}
                  >
                    Vertical Scroll
                  </button>
                  <button
                    onClick={() => setCurrentView('horizontal')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      currentView === 'horizontal' 
                        ? 'bg-primary-600 text-white' 
                        : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                    }`}
                  >
                    Horizontal Scroll
                  </button>
                </div>

                {/* Play Button */}
                <div className="flex justify-center">
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                  >
                    {isPlaying ? (
                      <Pause className="w-8 h-8 text-white" />
                    ) : (
                      <Play className="w-8 h-8 text-white ml-1" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full shadow-lg flex items-center justify-center"
            >
              <ZoomIn className="w-6 h-6 text-white" />
            </motion.div>
          </motion.div>

          {/* Stats and Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
                Modern PDF Experience
              </h3>
              <p className="text-secondary-600 leading-relaxed mb-6">
                Experience the future of PDF viewing with our modern, intuitive interface. 
                Designed with Material Design 3 principles, every interaction feels natural and responsive.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                  <span className="text-secondary-700">Flexible viewing options (vertical/horizontal)</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                  <span className="text-secondary-700">Intelligent zoom controls with reset</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                  <span className="text-secondary-700">Smart file management system</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                  <span className="text-secondary-700">Theme flexibility (light/dark/auto)</span>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-4 shadow-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                      <div className="text-white">
                        {stat.icon}
                      </div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-secondary-900">
                        {stat.value}
                      </div>
                      <div className="text-sm text-secondary-600">
                        {stat.label}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Download CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-center"
            >
              <h4 className="text-white text-xl font-semibold mb-2">
                Ready to Experience?
              </h4>
              <p className="text-primary-100 mb-4">
                Download now and transform your PDF reading experience
              </p>
              <a
                href="https://play.google.com/store/apps/details?id=com.palru.pdfviewer&hl=en"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-primary-700 px-6 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors duration-200"
              >
                <Download className="w-5 h-5 mr-2" />
                Download on Google Play
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Demo; 